import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { showToast } from '../components/Toast';
import { useSocket } from './SocketContext';
import apiClient from '../utils/apiClient';

const NotificationContext = createContext();

const notificationLabels = {
  new_order: 'New order',
  waiter_call: 'Waiter call',
  cleaning_request: 'Cleaning request',
  payment_done: 'Payment done',
  table_join: 'Table joined',
  order_ready: 'Order ready',
  info: 'Notification',
};

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch (error) {
    return null;
  }
}

function normalizeNotification(notification) {
  return {
    ...notification,
    read: Boolean(notification.read),
    createdAt: notification.createdAt || new Date().toISOString(),
  };
}

function isToday(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  return date.toDateString() === now.toDateString();
}

export function NotificationProvider({ children }) {
  const { socket, joinUserRoom, joinRoleRoom, leaveUserRoom, leaveRoleRoom } = useSocket();
  const [user, setUser] = useState(getStoredUser);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications]
  );

  const refreshNotifications = useCallback(async () => {
    const token = localStorage.getItem('token');
    const currentUser = getStoredUser();
    if (!token || !currentUser) {
      setNotifications([]);
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.get('/api/notifications/my');
      setNotifications((response.data || []).map(normalizeNotification).filter((item) => isToday(item.createdAt)));
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const syncUser = () => {
      setUser(getStoredUser());
    };

    syncUser();
    window.addEventListener('userUpdated', syncUser);
    window.addEventListener('storage', syncUser);
    return () => {
      window.removeEventListener('userUpdated', syncUser);
      window.removeEventListener('storage', syncUser);
    };
  }, []);

  useEffect(() => {
    refreshNotifications();
  }, [user?.id, user?.userId, refreshNotifications]);

  useEffect(() => {
    if (!socket || !user) return undefined;

    const userId = user.id || user.userId;
    if (userId) joinUserRoom(userId);
    if (user.role) joinRoleRoom(user.role);

    return () => {
      if (userId) leaveUserRoom(userId);
      if (user.role) leaveRoleRoom(user.role);
    };
  }, [socket, user, joinUserRoom, joinRoleRoom, leaveUserRoom, leaveRoleRoom]);

  useEffect(() => {
    if (!socket) return undefined;

    const handleNotification = (notification) => {
      const currentUser = getStoredUser();
      const currentUserId = currentUser?.id || currentUser?.userId;
      if (notification.userId && currentUserId && Number(notification.userId) !== Number(currentUserId)) {
        return;
      }

      const nextNotification = normalizeNotification(notification);
      if (!isToday(nextNotification.createdAt)) return;
      setNotifications((prev) => {
        if (prev.some((item) => item.id === nextNotification.id)) return prev;
        return [nextNotification, ...prev].slice(0, 50);
      });

      const title = notificationLabels[nextNotification.type] || notificationLabels.info;
      showToast(`${title}: ${nextNotification.message}`, nextNotification.type === 'order_ready' ? 'success' : 'info');
    };

    const handleRoleSignal = () => {
      refreshNotifications();
    };

    socket.on('notification:new', handleNotification);
    socket.on('notification:role', handleRoleSignal);

    return () => {
      socket.off('notification:new', handleNotification);
      socket.off('notification:role', handleRoleSignal);
    };
  }, [socket, refreshNotifications]);

  const markAsRead = useCallback(async (id) => {
    if (!id) return;
    setNotifications((prev) => prev.map((notification) => (
      notification.id === id ? { ...notification, read: true } : notification
    )));

    try {
      await apiClient.put(`/api/notifications/${id}/read`);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      refreshNotifications();
    }
  }, [refreshNotifications]);

  const markAllAsRead = useCallback(async () => {
    setNotifications((prev) => prev.map((notification) => ({ ...notification, read: true })));

    try {
      await apiClient.put('/api/notifications/read-all');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      refreshNotifications();
    }
  }, [refreshNotifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        notificationLabels,
        refreshNotifications,
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}
